// API base URL — injected at build time via VITE_API_URL (.env / .env.production).
// Falls back to localhost for local development.
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

// Get auth headers for authenticated requests
// NOTE: We use the ID token instead of access token because Cognito access tokens
// don't include user attributes like email by default. The API Gateway JWT Authorizer
// can validate both token types, and the backend needs the email claim.
export function getAuthHeaders(): HeadersInit {
  const idToken = localStorage.getItem('idToken')
  if (idToken) {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    }
  }
  return {
    'Content-Type': 'application/json',
  }
}

// Streaming URL — injected at build time via VITE_STREAMING_URL.
// Points to the CloudFront distribution that fronts the streaming Lambda Function URL.
export const STREAMING_URL = import.meta.env.VITE_STREAMING_URL || ''

export interface TestStep {
  step_number: number
  action: string
  test_data: string
  expected_result: string
}

export interface TestCase {
  id: string
  title: string
  type: 'happy_path' | 'sad_path' | 'edge_case'
  preconditions: string[]
  steps: TestStep[]
  priority: 'high' | 'medium' | 'low'
  tags: string[]
}

export interface TestSuite {
  test_suite_name: string
  requirement?: string
  generated_at?: string
  test_cases: TestCase[]
  _warning?: string
}

export interface JobStatus {
  jobId: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  progress: number
  statusMessage?: string
  testSuite?: TestSuite
  error?: string
  createdAt: number
  updatedAt: number
  completedAt?: number
  userId?: string
  userName?: string
  requirementText?: string
  jiraTicket?: string
  inputType?: 'text' | 'image' | 'figma'
  starred?: boolean
  testCaseCount?: number
  detailLevel?: 'concise' | 'standard' | 'detailed'
  targetDevices?: string[]
  targetBrowsers?: string[]
  figmaUrl?: string
}

export interface GenerateResponse {
  jobId: string
  status: string
  testSuite?: TestSuite
}

// Generate test cases from text requirement
export async function generateFromText(
  requirement: string,
  jiraTicket?: string,
  testCaseCount?: number,
  detailLevel?: 'concise' | 'standard' | 'detailed',
  customInstructions?: string,
  jiraIncludeFields?: Record<string, boolean>,
  autoCount?: boolean
): Promise<GenerateResponse> {
  const response = await fetch(`${API_BASE_URL}/generate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ requirement, jiraTicket, testCaseCount, detailLevel, customInstructions, jiraIncludeFields, autoCount }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to generate test cases')
  }

  return response.json()
}

// Image input type
export interface ImageInput {
  base64: string
  mediaType: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp'
  name?: string
}

// Generate test cases from single image (kept for backwards compatibility)
export async function generateFromImage(
  imageBase64: string,
  mediaType: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp',
  additionalContext?: string,
  jiraTicket?: string,
  testCaseCount?: number,
  detailLevel?: 'concise' | 'standard' | 'detailed',
  customInstructions?: string,
  jiraIncludeFields?: Record<string, boolean>,
  autoCount?: boolean
): Promise<GenerateResponse> {
  return generateFromImages([{ base64: imageBase64, mediaType }], additionalContext, jiraTicket, testCaseCount, detailLevel, customInstructions, jiraIncludeFields, autoCount)
}

// Generate test cases from multiple images
export async function generateFromImages(
  images: ImageInput[],
  additionalContext?: string,
  jiraTicket?: string,
  testCaseCount?: number,
  detailLevel?: 'concise' | 'standard' | 'detailed',
  customInstructions?: string,
  jiraIncludeFields?: Record<string, boolean>,
  autoCount?: boolean
): Promise<GenerateResponse> {
  const response = await fetch(`${API_BASE_URL}/generate-from-images`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ images, additionalContext, jiraTicket, testCaseCount, detailLevel, customInstructions, jiraIncludeFields, autoCount }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to generate test cases from images')
  }

  return response.json()
}

// Generate test cases from Figma URL
export async function generateFromFigma(
  figmaUrl: string,
  additionalContext?: string,
  jiraTicket?: string,
  testCaseCount?: number,
  detailLevel?: 'concise' | 'standard' | 'detailed',
  customInstructions?: string,
  jiraIncludeFields?: Record<string, boolean>,
  autoCount?: boolean
): Promise<GenerateResponse> {
  const response = await fetch(`${API_BASE_URL}/generate-from-figma`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ figmaUrl, additionalContext, jiraTicket, testCaseCount, detailLevel, customInstructions, jiraIncludeFields, autoCount }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to generate test cases from Figma')
  }

  return response.json()
}

// Get job status
export async function getJobStatus(jobId: string): Promise<JobStatus> {
  const response = await fetch(`${API_BASE_URL}/status/${jobId}`, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get job status')
  }

  return response.json()
}

// Export test suite
export async function exportTestSuite(
  jobId: string,
  format: 'testrail' | 'zephyr' | 'jira' | 'markdown' | 'json'
): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}/export`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ jobId, format }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to export test suite')
  }

  return response.blob()
}

// Poll for job completion
export async function pollJobStatus(
  jobId: string,
  onProgress?: (status: JobStatus) => void,
  pollInterval = 2000,
  maxAttempts = 60
): Promise<JobStatus> {
  let attempts = 0

  while (attempts < maxAttempts) {
    const status = await getJobStatus(jobId)

    if (onProgress) {
      onProgress(status)
    }

    if (status.status === 'completed' || status.status === 'failed') {
      return status
    }

    await new Promise((resolve) => setTimeout(resolve, pollInterval))
    attempts++
  }

  throw new Error('Job timed out')
}

// ============================================================================
// Test Case History API Types and Functions
// ============================================================================

export interface TestCaseHistoryItem {
  jobId: string
  testSuiteName: string | null
  testCaseCount: number
  createdAt: number
  completedAt?: number
  status: 'pending' | 'processing' | 'completed' | 'failed'
  userId?: string
  userName?: string
  inputType: 'text' | 'image' | 'figma'
  jiraTicket?: string
  typeBreakdown: {
    happyPath: number
    sadPath: number
    edgeCase: number
  }
  isOwner: boolean
  hasVideos: boolean
  videoCount: number
  starred?: boolean
}

// Get test case history
export async function getTestCaseHistory(
  filter: 'mine' | 'all' = 'mine'
): Promise<{ jobs: TestCaseHistoryItem[]; total: number }> {
  const url = filter === 'mine'
    ? `${API_BASE_URL}/test-cases?mine=true`
    : `${API_BASE_URL}/test-cases`

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get test case history')
  }

  return response.json()
}

// Delete a test case
export async function deleteTestCase(
  jobId: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/test-cases/${jobId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to delete test case')
  }

  return response.json()
}

// Toggle star on a test case
export async function toggleStar(jobId: string, starred: boolean): Promise<{ jobId: string; starred: boolean; deleted?: boolean }> {
  const response = await fetch(`${API_BASE_URL}/test-cases/${jobId}/star`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ starred }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to toggle star')
  }

  return response.json()
}

// ============================================================================
// Golden Examples API
// ============================================================================

export interface GoldenExample {
  id: string
  title: string
  type: 'happy_path' | 'sad_path' | 'edge_case'
  preconditions: string[]
  steps: TestStep[]
  priority: 'high' | 'medium' | 'low'
  tags: string[]
  savedAt: number
  sourceJobId?: string
}

export async function getGoldenExamples(): Promise<{ examples: GoldenExample[]; limit: number }> {
  const response = await fetch(`${API_BASE_URL}/settings/golden-examples`, {
    headers: getAuthHeaders(),
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to fetch golden examples')
  }
  return response.json()
}

export async function saveGoldenExamples(
  testCases: TestCase[],
  sourceJobId?: string
): Promise<{ examples: GoldenExample[] }> {
  const response = await fetch(`${API_BASE_URL}/settings/golden-examples`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ testCases, sourceJobId }),
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to save golden examples')
  }
  return response.json()
}

export async function deleteGoldenExample(exampleId: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE_URL}/settings/golden-examples/${exampleId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to delete golden example')
  }
  return response.json()
}

// ============================================================================
// Test Case Video Upload API Types and Functions
// ============================================================================

export interface VideoMetadata {
  videoId: string
  fileName: string
  downloadUrl: string
  uploadedAt: number
  fileSize: number
  contentType: string
}

export interface PresignedUploadResponse {
  uploadUrl: string
  videoId: string
  s3Key: string
  expiresIn: number
}

// Request a presigned URL for uploading a video
export async function requestVideoUploadUrl(params: {
  jobId: string
  fileName: string
  contentType: string
  fileSize: number
}): Promise<PresignedUploadResponse> {
  const response = await fetch(`${API_BASE_URL}/test-cases/videos/presign`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get upload URL')
  }

  return response.json()
}

// Confirm video upload completion
export async function confirmVideoUpload(
  jobId: string,
  params: {
    videoId: string
    s3Key: string
    fileName: string
    fileSize: number
    contentType: string
  }
): Promise<{ success: boolean; video: { videoId: string; fileName: string; uploadedAt: number } }> {
  const response = await fetch(`${API_BASE_URL}/test-cases/${jobId}/videos`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to confirm video upload')
  }

  return response.json()
}

// Get videos for a job
export async function getJobVideos(jobId: string): Promise<{ videos: VideoMetadata[] }> {
  const response = await fetch(`${API_BASE_URL}/test-cases/${jobId}/videos`, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get videos')
  }

  return response.json()
}

// Delete a video
export async function deleteJobVideo(
  jobId: string,
  videoId: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/test-cases/${jobId}/videos/${videoId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to delete video')
  }

  return response.json()
}

// Upload a video file (convenience function that handles the full flow)
export async function uploadVideo(
  jobId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<VideoMetadata> {
  // 1. Get presigned URL
  const presignResponse = await requestVideoUploadUrl({
    jobId,
    fileName: file.name,
    contentType: file.type,
    fileSize: file.size,
  })

  // 2. Upload to S3 using XMLHttpRequest for progress tracking
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100)
        onProgress(progress)
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'))
    })

    xhr.open('PUT', presignResponse.uploadUrl)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.send(file)
  })

  // 3. Confirm upload
  const confirmResponse = await confirmVideoUpload(jobId, {
    videoId: presignResponse.videoId,
    s3Key: presignResponse.s3Key,
    fileName: file.name,
    fileSize: file.size,
    contentType: file.type,
  })

  return {
    videoId: confirmResponse.video.videoId,
    fileName: confirmResponse.video.fileName,
    uploadedAt: confirmResponse.video.uploadedAt,
    fileSize: file.size,
    contentType: file.type,
    downloadUrl: '', // Will be fetched when needed via getJobVideos
  }
}

// ============================================================================
// Offers Audit API Types and Functions
// ============================================================================

export interface AuditStatusSummary {
  totalTDAs: number
  processed: number
  passed: number
  warned: number
  failed: number
}

export interface AuditJobStatus {
  jobId: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  progress: number
  message?: string
  // Server-computed "X of Y complete" for the loader's current activity
  liveCounts?: { completed: number; total: number }
  summary?: AuditStatusSummary
  application?: 'BAT' | 'RCT'
  error?: string
  tdas?: string[]
  languages?: ('en' | 'es')[]
  pages?: ('homepage' | 'offers' | 'offerDetails' | 'vehicleInfoSupra' | 'vehicleInfoLandCruiser')[]
  hasExcelData?: boolean
  createdAt?: number
  updatedAt?: number
  completedAt?: number
  isOwner?: boolean
  resultsSummary?: {
    totalScreenshots: number
    passed: number
    warnings: number
    failed: number
    errors: number
  }
}

export interface AuditReport {
  jobId: string
  application: 'BAT' | 'RCT'
  completedAt: string
  summary: AuditStatusSummary
  results: AuditResult[]
}

export interface AuditResult {
  tda: string
  language: 'en' | 'es'
  page: 'homepage' | 'offers'
  status: 'passed' | 'warned' | 'failed'
  issues: AuditIssue[]
  baseline?: BaselineInfo
}

export interface AuditIssue {
  type: string
  severity: 'error' | 'warning' | 'info'
  description: string
  element?: string
  expected?: string
  actual?: string
}

export interface BaselineInfo {
  exists: boolean
  lastUpdated?: string
  hash?: string
}

export interface KnownIssue {
  issueId: string
  tda: string
  page: string
  issueType: string
  description: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// Helper function to convert File to base64
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // Remove data URL prefix (e.g., "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,")
      const base64 = result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

// Start a new audit job
export async function startOffersAudit(params: {
  application: 'BAT' | 'RCT'
  tdas?: string[]
  languages?: ('en' | 'es')[]
  pages?: ('homepage' | 'offers' | 'offerDetails' | 'vehicleInfoSupra' | 'vehicleInfoLandCruiser')[]
  excelFile?: File
}): Promise<{ jobId: string; status: string }> {
  const { application, tdas, languages, pages, excelFile } = params

  const requestBody: {
    application: 'BAT' | 'RCT'
    tdas?: string[]
    languages?: ('en' | 'es')[]
    pages?: ('homepage' | 'offers' | 'offerDetails' | 'vehicleInfoSupra' | 'vehicleInfoLandCruiser')[]
    excelFile?: string // base64 encoded
  } = {
    application,
    tdas,
    languages,
    pages,
  }

  // Convert Excel file to base64 if provided
  if (excelFile) {
    requestBody.excelFile = await fileToBase64(excelFile)
  }

  const response = await fetch(`${API_BASE_URL}/offers-audit/start`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to start offers audit')
  }

  return response.json()
}

// Get audit job status
export async function getAuditStatus(jobId: string): Promise<AuditJobStatus> {
  const response = await fetch(`${API_BASE_URL}/offers-audit/status/${jobId}`, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get audit status')
  }

  return response.json()
}

// Get full audit results
export async function getAuditResults(jobId: string): Promise<AuditReport> {
  const response = await fetch(`${API_BASE_URL}/offers-audit/results/${jobId}`, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get audit results')
  }

  return response.json()
}

// Save baseline(s)
export async function saveBaseline(params: {
  jobId: string
  tda?: string
  language?: 'en' | 'es'
  page?: 'homepage' | 'offers' | 'offerDetails' | 'vehicleInfoSupra' | 'vehicleInfoLandCruiser'
  offerId?: string
  approveAll?: boolean
}): Promise<{ success: boolean; baselinesSaved: number }> {
  const response = await fetch(`${API_BASE_URL}/offers-audit/baseline`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to save baseline')
  }

  return response.json()
}

// Delete baseline(s) for a specific tda/language/page/offerId
export async function deleteBaseline(params: {
  tda: string
  language: 'en' | 'es'
  page: 'homepage' | 'offers' | 'offerDetails' | 'vehicleInfoSupra' | 'vehicleInfoLandCruiser'
  offerId?: string
  application?: 'BAT' | 'RCT'
}): Promise<{ success: boolean; deletedCount: number }> {
  const queryParams = new URLSearchParams({
    tda: params.tda,
    language: params.language,
    page: params.page,
    application: params.application || 'BAT',
  })
  if (params.offerId) {
    queryParams.set('offerId', params.offerId)
  }

  const response = await fetch(`${API_BASE_URL}/offers-audit/baseline?${queryParams}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to delete baseline')
  }

  return response.json()
}

// Add/update known issue
export async function saveKnownIssue(params: {
  tda: string
  page: string
  issueType: string
  description: string
  isActive?: boolean
}): Promise<{ issueId: string; success: boolean }> {
  const response = await fetch(`${API_BASE_URL}/offers-audit/known-issue`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to save known issue')
  }

  return response.json()
}

// Get all known issues
export async function getKnownIssues(tda?: string): Promise<{ total: number; activeCount: number; issues: KnownIssue[] }> {
  const url = tda
    ? `${API_BASE_URL}/offers-audit/known-issues?tda=${encodeURIComponent(tda)}`
    : `${API_BASE_URL}/offers-audit/known-issues`

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get known issues')
  }

  return response.json()
}

// Export audit results.
// CSV and JSON return a Blob for direct download.
// PDF returns { downloadUrl, filename } pointing to a presigned S3 URL (avoids API Gateway 10MB limit).
export async function exportAuditResults(params: {
  jobId: string
  format: 'csv' | 'json' | 'pdf'
}): Promise<Blob | { downloadUrl: string; filename: string }> {
  const response = await fetch(`${API_BASE_URL}/offers-audit/export`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to export audit results')
  }

  if (params.format === 'pdf') {
    return response.json() as Promise<{ downloadUrl: string; filename: string }>
  }
  return response.blob()
}

// Cancel audit job
export async function cancelAudit(jobId: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE_URL}/offers-audit/cancel/${jobId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to cancel audit')
  }

  return response.json()
}

// Poll for audit job completion
export async function pollAuditStatus(
  jobId: string,
  onProgress?: (status: AuditJobStatus) => void,
  pollInterval = 3000,
  maxAttempts = 120
): Promise<AuditJobStatus> {
  let attempts = 0

  while (attempts < maxAttempts) {
    const status = await getAuditStatus(jobId)

    if (onProgress) {
      onProgress(status)
    }

    if (status.status === 'completed' || status.status === 'failed') {
      return status
    }

    await new Promise((resolve) => setTimeout(resolve, pollInterval))
    attempts++
  }

  throw new Error('Audit job timed out')
}

// Audit history item type
export interface AuditHistoryItem {
  jobId: string
  application: 'BAT' | 'RCT'
  status: 'pending' | 'processing' | 'completed' | 'failed'
  createdAt: number
  completedAt?: number
  resultsSummary?: {
    totalScreenshots: number
    passed: number
    warnings: number
    failed: number
    errors: number
  }
  tdas: string[]
  languages: ('en' | 'es')[]
  pages: ('homepage' | 'offers' | 'offerDetails' | 'vehicleInfoSupra' | 'vehicleInfoLandCruiser')[]
  hasExcelData: boolean
  isOwner: boolean
  userName?: string
}

// Get audit history (last 30 days, max 50 results)
export async function getAuditHistory(
  filter: 'mine' | 'all' = 'mine'
): Promise<{ total: number; jobs: AuditHistoryItem[] }> {
  const url = filter === 'mine'
    ? `${API_BASE_URL}/offers-audit/history?mine=true`
    : `${API_BASE_URL}/offers-audit/history`

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get audit history')
  }

  return response.json()
}

// Delete an audit and all associated data
export async function deleteAudit(jobId: string): Promise<{ success: boolean; message: string; deletedRecords: number }> {
  const response = await fetch(`${API_BASE_URL}/offers-audit/${jobId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to delete audit')
  }

  return response.json()
}

// ==========================================
// Risk Prioritization API Functions
// ==========================================

export interface ProfileSummary {
  profileId: string
  name: string
  createdAt: number
  updatedAt: number
  testCaseCount: number
  areaCount: number
}

export interface ParsedTestCase {
  id: string
  title: string
  area: string
}

export interface ParsedArea {
  areaId: string
  name: string
  testCases: ParsedTestCase[]
  testCaseCount: number
}

export interface ParsedTestLibrary {
  totalTestCases: number
  areas: ParsedArea[]
  sourceFormat: string
  parsedAt: number
}

export interface AreaContext {
  historicalBugs: string
  businessCritical: string
  dependencies: string
  lastTested: string
}

export interface RiskWeights {
  changeProximity: number
  historicalBugs: number
  businessCritical: number
  dependencies: number
  lastTested: number
}

export interface AppProfile {
  profileId: string
  name: string
  testLibrary: ParsedTestLibrary
  areaContexts: Record<string, AreaContext>
  riskWeights: RiskWeights
  createdAt: number
  updatedAt: number
}

export interface RiskAnalysisResults {
  analysisId: string
  profileId: string
  profileName: string
  jiraTickets: string[]
  rankedAreas: RankedArea[]
  summary: AnalysisSummary
  weightsUsed?: RiskWeights
  createdAt: number
  processingTimeMs: number
}

export interface RankedArea {
  areaId: string
  areaName: string
  riskScore: number
  riskLevel: 'high' | 'medium' | 'low'
  reasoning: string
  factorScores: {
    changeProximity: number
    historicalBugs: number
    businessCritical: number
    dependencies: number
    lastTested: number
  }
  testCases: ParsedTestCase[]
  matchedTickets: string[]
}

export interface AnalysisSummary {
  totalAreas: number
  highRiskAreas: number
  mediumRiskAreas: number
  lowRiskAreas: number
  totalTestCases: number
}

export interface AnalysisStatus {
  analysisId: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  progress: number
  error?: string
}

// Profile Management
export async function createProfile(name: string): Promise<{ profileId: string }> {
  const response = await fetch(`${API_BASE_URL}/risk-prioritization/profiles`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ name }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to create profile')
  }

  return response.json()
}

export async function listProfiles(): Promise<{ profiles: ProfileSummary[] }> {
  const response = await fetch(`${API_BASE_URL}/risk-prioritization/profiles`, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to list profiles')
  }

  return response.json()
}

export async function getProfile(profileId: string): Promise<AppProfile> {
  const response = await fetch(`${API_BASE_URL}/risk-prioritization/profiles/${profileId}`, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get profile')
  }

  return response.json()
}

export async function updateProfile(profileId: string, updates: Partial<AppProfile>): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/risk-prioritization/profiles/${profileId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to update profile')
  }
}

export async function deleteProfile(profileId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/risk-prioritization/profiles/${profileId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to delete profile')
  }
}

export async function uploadTestLibrary(
  profileId: string,
  csvFile: File,
  base64Content: string,
  mode: 'replace' | 'append' = 'replace'
): Promise<{ success: boolean; testLibrary: ParsedTestLibrary; mode: string; added: number; total: number; warning?: string }> {
  const response = await fetch(`${API_BASE_URL}/risk-prioritization/profiles/${profileId}/library`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      csvContent: base64Content,
      fileName: csvFile.name,
      mode,
    }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to upload test library')
  }

  return response.json()
}

// Analysis
export async function startAnalysis(params: {
  profileId: string
  jiraTickets: string[]
}): Promise<{ analysisId: string }> {
  const response = await fetch(`${API_BASE_URL}/risk-prioritization/analyze`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to start analysis')
  }

  return response.json()
}

export async function getAnalysisStatus(analysisId: string): Promise<AnalysisStatus> {
  const response = await fetch(`${API_BASE_URL}/risk-prioritization/analysis/${analysisId}/status`, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get analysis status')
  }

  return response.json()
}

export async function getAnalysisResults(analysisId: string): Promise<RiskAnalysisResults> {
  const response = await fetch(`${API_BASE_URL}/risk-prioritization/analysis/${analysisId}/results`, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get analysis results')
  }

  return response.json()
}

export async function exportAnalysis(analysisId: string, format: 'csv' | 'json'): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}/risk-prioritization/analysis/${analysisId}/export`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ format }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to export analysis')
  }

  return response.blob()
}

export async function getAnalysisHistory(profileId: string): Promise<{ analyses: unknown[] }> {
  const response = await fetch(`${API_BASE_URL}/risk-prioritization/profiles/${profileId}/history`, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get analysis history')
  }

  return response.json()
}

// ============================================================================
// RT Audit API Types and Functions
// ============================================================================

export interface RTAuditDealerResult {
  dealerCode: string
  dealerUrl: string
  rentalPageUrl: string | null
  rentalPageUrls?: string[]
  c1_logo: string
  c2_link: string
  c3_phone: string
  c4_about: string
  c5_pricing: string
  c6_reservations: string
  c7_displayed: string
  subtotal: number
  totalPoints: number
  passStatus: 'Pass' | 'Fail'
  screenshotKeys: string[]
  screenshotUrls?: string[]
  cached?: boolean
  capturedAt?: number
  notes: string
  flags: string[]
  confidence: string
}

export interface RTAuditJobStatus {
  jobId: string
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'
  progress: number
  totalDealers: number
  completedDealers: number
  failedDealers: number
  statusMessage: string
  error?: string
  createdAt: number
  updatedAt?: number
  completedAt?: number
  summary?: {
    totalAudited: number
    passed: number
    failed: number
    errors: number
    passRate: number
    criteriaPassRates: Record<string, number>
    averageScore: number
  }
  driveUploadStatus?: 'pending' | 'uploading' | 'completed' | 'failed'
  driveRootFolderUrl?: string
  driveFolderExists?: boolean | null
}

export interface RTAuditHistoryItem {
  jobId: string
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'
  totalDealers: number
  completedDealers: number
  failedDealers: number
  progress: number
  statusMessage: string
  createdAt: number
  updatedAt?: number
  completedAt?: number
  summary?: RTAuditJobStatus['summary']
  isOwner: boolean
  userName?: string
}

// Start a new RT Audit
export async function startRTAudit(params: {
  csvData: string
  fileName?: string
}): Promise<{ jobId: string; status: string }> {
  const response = await fetch(`${API_BASE_URL}/rt-audit/start`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    const error = await response.json()
    if (response.status === 409 && error.activeJobId) {
      const err = new Error(error.error || 'You already have a running RT audit') as Error & { activeJobId?: string }
      err.activeJobId = error.activeJobId
      throw err
    }
    throw new Error(error.error || 'Failed to start RT audit')
  }

  return response.json()
}

// Get RT Audit status
export async function getRTAuditStatus(jobId: string): Promise<RTAuditJobStatus> {
  const response = await fetch(`${API_BASE_URL}/rt-audit/status/${jobId}`, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    // Carry the status so the poller can tell a lost job (403/404) from a
    // passing failure (401 before a token refresh, 5xx, network) worth retrying.
    throw Object.assign(new Error(error.error || 'Failed to get RT audit status'), { status: response.status })
  }

  return response.json()
}

// Get RT Audit results
export async function getRTAuditResults(jobId: string): Promise<{
  jobId: string
  status: string
  summary: RTAuditJobStatus['summary']
  totalDealers: number
  completedDealers: number
  results: RTAuditDealerResult[]
}> {
  const response = await fetch(`${API_BASE_URL}/rt-audit/results/${jobId}`, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get RT audit results')
  }

  return response.json()
}

// Export RT Audit results as XLSX
export async function exportRTAudit(jobId: string): Promise<{ blob: Blob; fileName: string }> {
  const response = await fetch(`${API_BASE_URL}/rt-audit/export`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ jobId }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to export RT audit')
  }

  const data = await response.json()
  const fileResponse = await fetch(data.downloadUrl)
  if (!fileResponse.ok) {
    throw new Error('Failed to download export file')
  }

  return { blob: await fileResponse.blob(), fileName: data.fileName }
}

// Export RT Audit screenshots as ZIP
export async function exportRTAuditScreenshots(
  jobId: string,
  signal?: AbortSignal
): Promise<{ blob: Blob; fileName: string }> {
  const response = await fetch(`${API_BASE_URL}/rt-audit/export-screenshots`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ jobId }),
    signal,
  })

  if (!response.ok) {
    let errorMessage = 'Failed to export screenshots'
    try {
      const error = await response.json()
      errorMessage = error.error || errorMessage
    } catch {
      // Non-JSON error body (e.g., API Gateway 502)
    }
    throw new Error(errorMessage)
  }

  const data = await response.json()
  const fileResponse = await fetch(data.downloadUrl, { signal })
  if (!fileResponse.ok) {
    throw new Error('Failed to download screenshot archive')
  }

  return { blob: await fileResponse.blob(), fileName: data.fileName }
}

// Capture-cache status for a set of dealers (pre-run view).
export async function getRTAuditCacheStatus(
  dealers: Array<{ dealerCode: string; dealerUrl: string }>
): Promise<{ statuses: Record<string, { cached: boolean; capturedAt: number | null }>; cachedCount: number; freshCount: number; ttlDays: number }> {
  const response = await fetch(`${API_BASE_URL}/rt-audit/cache-status`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ dealers }),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to get capture-cache status')
  }
  return response.json()
}

// Total number of dealers that currently have a cached capture (landing-page callout).
export async function getRTAuditCacheSummary(): Promise<{ totalCached: number; ttlDays: number }> {
  const response = await fetch(`${API_BASE_URL}/rt-audit/cache-summary`, {
    headers: getAuthHeaders(),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to get capture-cache summary')
  }
  return response.json()
}

// Clear the entire capture cache (gated behind a confirmation dialog in the UI).
export async function clearRTAuditCache(): Promise<{ success: boolean; objectsDeleted: number }> {
  const response = await fetch(`${API_BASE_URL}/rt-audit/clear-cache`, {
    method: 'POST',
    headers: getAuthHeaders(),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to clear capture cache')
  }
  return response.json()
}

// Cancel a running RT Audit
export async function cancelRTAudit(jobId: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE_URL}/rt-audit/cancel/${jobId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to cancel RT audit')
  }

  return response.json()
}

// Resume a failed/completed RT Audit (re-process only error dealers)
export async function resumeRTAudit(jobId: string): Promise<{ jobId: string; status: string; resumedDealers: number }> {
  const response = await fetch(`${API_BASE_URL}/rt-audit/resume/${jobId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to resume RT audit')
  }

  return response.json()
}

// Retry a failed Drive upload for an RT Audit job
export async function retryRTAuditDriveUpload(jobId: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE_URL}/rt-audit/retry-drive-upload/${jobId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to retry drive upload')
  }

  return response.json()
}

// Get RT Audit history
export async function getRTAuditHistory(
  filter: 'mine' | 'all' = 'mine'
): Promise<{ total: number; jobs: RTAuditHistoryItem[] }> {
  const url = filter === 'mine'
    ? `${API_BASE_URL}/rt-audit/history?mine=true`
    : `${API_BASE_URL}/rt-audit/history`

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get RT audit history')
  }

  return response.json()
}

// Delete an RT Audit
export async function deleteRTAudit(jobId: string): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/rt-audit/${jobId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to delete RT audit')
  }

  return response.json()
}

// ==========================================
// Settings API
// ==========================================

export interface FigmaConnectionInfo {
  connected: boolean
  figmaUserName?: string
  figmaUserEmail?: string
  connectedAt?: number
  status: 'active' | 'expired' | 'disconnected'
}

export async function getFigmaStatus(): Promise<FigmaConnectionInfo> {
  const response = await fetch(`${API_BASE_URL}/settings/figma/status`, {
    headers: getAuthHeaders(),
  })
  if (!response.ok) {
    return { connected: false, status: 'disconnected' }
  }
  return response.json()
}

export async function exchangeFigmaCode(code: string): Promise<{ success: boolean; figmaUserName: string; figmaUserEmail: string }> {
  const response = await fetch(`${API_BASE_URL}/settings/figma/exchange`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ code }),
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to connect Figma account')
  }
  return response.json()
}

export async function disconnectFigma(): Promise<{ disconnected: boolean }> {
  const response = await fetch(`${API_BASE_URL}/settings/figma/disconnect`, {
    method: 'POST',
    headers: getAuthHeaders(),
  })
  if (!response.ok) {
    throw new Error('Failed to disconnect Figma')
  }
  return response.json()
}

// ==========================================
// Jira Settings API
// ==========================================

export interface JiraInstanceInfo {
  cloudId: string
  cloudUrl: string
  cloudName: string
  active: boolean
}

export interface JiraConnectionDetail {
  connectionId: string
  accountId: string
  userName: string
  userEmail: string
  connectedAt: number
  status: 'active' | 'expired'
  authType?: 'oauth' | 'apitoken'
  apiTokenEmail?: string
  instances: JiraInstanceInfo[]
  savedProjectKeys?: string[]
}

export interface JiraConnectionInfo {
  connected: boolean
  jiraUserName?: string
  jiraUserEmail?: string
  jiraCloudUrl?: string
  connectedAt?: number
  status: 'active' | 'expired' | 'disconnected'
  instances?: JiraInstanceInfo[]
  connections?: JiraConnectionDetail[]
}

export async function getJiraStatus(): Promise<JiraConnectionInfo> {
  const response = await fetch(`${API_BASE_URL}/settings/jira/status`, {
    headers: getAuthHeaders(),
  })
  if (!response.ok) {
    return { connected: false, status: 'disconnected' }
  }
  return response.json()
}

export async function exchangeJiraCode(code: string): Promise<{ success: boolean; jiraUserName: string; jiraUserEmail: string; jiraCloudUrl: string; connectionId?: string; connections?: JiraConnectionDetail[] }> {
  const response = await fetch(`${API_BASE_URL}/settings/jira/exchange`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ code }),
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to connect Jira account')
  }
  return response.json()
}

export async function disconnectJira(): Promise<{ disconnected: boolean }> {
  const response = await fetch(`${API_BASE_URL}/settings/jira/disconnect`, {
    method: 'POST',
    headers: getAuthHeaders(),
  })
  if (!response.ok) {
    throw new Error('Failed to disconnect Jira')
  }
  return response.json()
}

export async function disconnectJiraConnection(connectionId: string): Promise<{ disconnected: boolean; connections: JiraConnectionDetail[] }> {
  const response = await fetch(`${API_BASE_URL}/settings/jira/connections/${encodeURIComponent(connectionId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
  if (!response.ok) {
    throw new Error('Failed to disconnect Jira connection')
  }
  return response.json()
}

export async function addJiraApiToken(instanceUrl: string, email: string, apiToken: string): Promise<{ success: boolean; connections: JiraConnectionDetail[] }> {
  const response = await fetch(`${API_BASE_URL}/settings/jira/api-token`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ instanceUrl, email, apiToken }),
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to connect with API token')
  }
  return response.json()
}

export async function testJiraApiToken(instanceUrl: string, email: string, apiToken: string): Promise<{ success: boolean; userName?: string; userEmail?: string; siteName?: string; error?: string }> {
  const response = await fetch(`${API_BASE_URL}/settings/jira/api-token/test`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ instanceUrl, email, apiToken }),
  })
  return response.json()
}

export async function updateJiraApiToken(connectionId: string, apiToken: string): Promise<{ success: boolean; connections: JiraConnectionDetail[] }> {
  const response = await fetch(`${API_BASE_URL}/settings/jira/api-token/${encodeURIComponent(connectionId)}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ apiToken }),
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to update API token')
  }
  return response.json()
}

export async function toggleJiraInstance(cloudId: string, active: boolean): Promise<{ instances: JiraInstanceInfo[] }> {
  const response = await fetch(`${API_BASE_URL}/settings/jira/instances/${encodeURIComponent(cloudId)}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ active }),
  })
  if (!response.ok) {
    throw new Error('Failed to update instance')
  }
  return response.json()
}

// ==========================================
// Jira Issue Preview API
// ==========================================

export interface JiraIssuePreview {
  key: string
  summary: string
  description: string
  acceptanceCriteria?: string
  status: { name: string; color: string }
  priority: { name: string }
  type: { name: string }
  labels: string[]
  components: string[]
  parentSummary: string | null
  subtasks: { key: string; summary: string }[]
  linkedIssues: { key: string; summary: string; linkType: string }[]
  comments?: { author: string; text: string; created: string }[]
  attachments?: { filename: string; mimeType: string; size: number }[]
  unsupportedAttachments?: { count: number; formats: string[] }
  cloudUrl?: string
  contextForAI: string
}

export async function fetchJiraIssue(issueKey: string): Promise<JiraIssuePreview> {
  const response = await fetch(`${API_BASE_URL}/jira/issue/${encodeURIComponent(issueKey)}`, {
    headers: getAuthHeaders(),
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || `Failed to fetch ticket (${response.status})`)
  }
  return response.json()
}

// ==========================================
// Jira Search API
// ==========================================

export interface JiraSearchResult {
  key: string
  summary: string
  description?: string
  status: { name: string; color: string }
  type: { name: string }
  subtaskCount?: number
  linkedIssueCount?: number
  commentCount?: number
  attachmentCount?: number
  unsupportedAttachments?: { count: number; formats: string[] }
}

export async function searchJiraTickets(query: string, startAt = 0): Promise<{ results: JiraSearchResult[]; hasMore?: boolean }> {
  const response = await fetch(`${API_BASE_URL}/jira/search?query=${encodeURIComponent(query)}&startAt=${startAt}`, {
    headers: getAuthHeaders(),
  })
  if (!response.ok) return { results: [] }
  return response.json()
}

// ==========================================
// Jira Board & Sprint API
// ==========================================

export interface JiraBoard {
  id: number
  name: string
  type: string
  projectKey: string
}

export interface JiraSprint {
  id: number
  name: string
  state: string
  startDate?: string
  endDate?: string
  goal?: string
}

export interface JiraSprintIssue {
  key: string
  summary: string
  status: { name: string; color: string }
  type: { name: string }
  priority: { name: string }
}

export type BoardsReason = 'no_token' | 'token_expired' | 'no_cloud_info' | 'invalid_cloud_id' | 'api_forbidden' | 'api_error'

export async function getJiraBoards(projectKey?: string, cloudId?: string): Promise<{ boards: JiraBoard[]; reason?: BoardsReason; totalAvailable?: number }> {
  const params = new URLSearchParams()
  if (projectKey) params.set('projectKeyOrId', projectKey)
  if (cloudId) params.set('cloudId', cloudId)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const response = await fetch(`${API_BASE_URL}/jira/boards${qs}`, {
    headers: getAuthHeaders(),
  })
  if (!response.ok) return { boards: [], reason: 'api_error' }
  return response.json()
}

export async function addSavedProjectKey(connectionId: string, projectKey: string): Promise<{ keys: string[] }> {
  const response = await fetch(`${API_BASE_URL}/settings/jira/connections/${encodeURIComponent(connectionId)}/project-keys`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ projectKey }),
  })
  if (!response.ok) return { keys: [] }
  return response.json()
}

export async function removeSavedProjectKey(connectionId: string, projectKey: string): Promise<{ keys: string[] }> {
  const response = await fetch(`${API_BASE_URL}/settings/jira/connections/${encodeURIComponent(connectionId)}/project-keys/${encodeURIComponent(projectKey)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
  if (!response.ok) return { keys: [] }
  return response.json()
}

export async function getJiraSprints(boardId: number, state?: string, cloudId?: string): Promise<{ sprints: JiraSprint[] }> {
  const params = new URLSearchParams()
  if (state) params.set('state', state)
  if (cloudId) params.set('cloudId', cloudId)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const response = await fetch(`${API_BASE_URL}/jira/boards/${boardId}/sprints${qs}`, {
    headers: getAuthHeaders(),
  })
  if (!response.ok) return { sprints: [] }
  return response.json()
}

export async function getJiraSprintIssues(sprintId: number, cloudId?: string): Promise<{ issues: JiraSprintIssue[]; total: number }> {
  const params = cloudId ? `?cloudId=${encodeURIComponent(cloudId)}` : ''
  const response = await fetch(`${API_BASE_URL}/jira/sprints/${sprintId}/issues${params}`, {
    headers: getAuthHeaders(),
  })
  if (!response.ok) return { issues: [], total: 0 }
  return response.json()
}

export async function searchJiraByJql(
  jql: string,
  startAt = 0,
  maxResults = 50
): Promise<{ issues: JiraSprintIssue[]; total: number; hasMore: boolean; error?: string }> {
  const response = await fetch(`${API_BASE_URL}/jira/jql`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ jql, startAt, maxResults }),
  })
  // Backend returns structured JSON even on 400 (invalid JQL). Only guard against non-JSON responses.
  if (!response.ok && response.status !== 400) {
    return { issues: [], total: 0, hasMore: false, error: 'Failed to execute JQL query' }
  }
  return response.json()
}

// ==========================================
// Risk Prioritization Auto-Run API
// ==========================================

export interface AutoRunConfig {
  enabled: boolean
  boardId: number
  boardName?: string
  lastSprintId?: number
  lastSprintName?: string
  lastAutoRunAt?: number
  lastAutoRunAnalysisId?: string
  lastAutoRunError?: string
}

export async function updateAutoRun(
  profileId: string,
  config: { enabled: boolean; boardId?: number; boardName?: string }
): Promise<{ autoRun: AutoRunConfig }> {
  const response = await fetch(`${API_BASE_URL}/risk-prioritization/profiles/${profileId}/auto-run`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(config),
  })
  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.error || 'Failed to update auto-run configuration')
  }
  return response.json()
}

export async function getAutoRun(profileId: string): Promise<{ autoRun: AutoRunConfig | null }> {
  const response = await fetch(`${API_BASE_URL}/risk-prioritization/profiles/${profileId}/auto-run`, {
    headers: getAuthHeaders(),
  })
  if (!response.ok) return { autoRun: null }
  return response.json()
}

// ==========================================
// User Preferences API
// ==========================================

export type ThemePreference = 'light' | 'dark' | 'auto'

// Fetches the authenticated user's stored theme preference.
// Returns { theme: 'auto' } as a graceful default whenever the API is
// unavailable, the user is unauthenticated, or the response is malformed.
// We never throw here because the caller (ThemeContext) treats this as a
// best-effort hydration step — the cookie has already supplied a usable
// value and a failure should not break the UI.
export async function getPreferences(): Promise<{ theme: ThemePreference }> {
  try {
    const response = await fetch(`${API_BASE_URL}/settings/preferences`, {
      headers: getAuthHeaders(),
    })
    if (!response.ok) return { theme: 'auto' }
    const data = (await response.json()) as { theme?: unknown }
    if (data && (data.theme === 'light' || data.theme === 'dark' || data.theme === 'auto')) {
      return { theme: data.theme }
    }
    return { theme: 'auto' }
  } catch {
    return { theme: 'auto' }
  }
}

// Persists the user's theme preference to the backend.
// Throws on non-OK so the caller can surface an error toast and decide
// whether to retry. The local cookie/UI state should NOT be reverted on
// failure — the cookie always wins for UX continuity, the server reconciles
// next session.
export async function putPreferences(
  theme: ThemePreference
): Promise<{ success: true; theme: ThemePreference }> {
  const response = await fetch(`${API_BASE_URL}/settings/preferences`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ theme }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to save preferences')
  }

  return response.json()
}

// ==========================================
// Test Case Update Types
// ==========================================

export interface TCUpdateSummary {
  totalAnalyzed: number
  toUpdate: number
  newGaps: number
  obsolete: number
  unchanged: number
  jiraTicketsRequested?: number
  jiraContextsResolved?: number
}

export interface SuggestedChanges {
  title?: string | null
  steps?: { step_number: number; action: string; test_data: string; expected_result: string }[] | null
  expectedResult?: string | null
  preconditions?: string | null
}

export interface TCUpdateRecommendation {
  existingTestCaseId: string
  existingTitle: string
  area: string
  impactLevel: 'high' | 'medium' | 'low'
  affectedTickets: string[]
  changeType: string
  recommendation: string
  suggestedChanges: SuggestedChanges
}

export interface TCNewTestCase {
  area: string
  title: string
  rationale: string
  relatedTickets: string[]
  priority: 'high' | 'medium' | 'low'
  type: 'happy_path' | 'sad_path' | 'edge_case'
  preconditions: string[]
  steps: { step_number: number; action: string; test_data: string; expected_result: string }[]
}

export interface TCObsoleteCase {
  existingTestCaseId: string
  existingTitle: string
  area: string
  reason: string
  relatedTickets: string[]
  confidence: 'high' | 'medium' | 'low'
}

export interface TCUpdateResults {
  updateJobId: string
  casesToUpdate: TCUpdateRecommendation[]
  newTestCases: TCNewTestCase[]
  obsoleteTestCases: TCObsoleteCase[]
  summary: TCUpdateSummary
  analyzedAt: number
  modelUsed: string
  inputTokens: number
  outputTokens: number
}

export interface TCRewrittenCase {
  existingTestCaseId: string
  title: string
  type: 'happy_path' | 'sad_path' | 'edge_case'
  preconditions: string[]
  steps: { step_number: number; action: string; test_data: string; expected_result: string }[]
  priority: 'high' | 'medium' | 'low'
  tags: string[]
}

export interface TCRewriteResult {
  updateJobId: string
  rewrittenCases: TCRewrittenCase[]
  totalInputTokens: number
  totalOutputTokens: number
  rewrittenAt: number
}

// ==========================================
// Test Case Update API
// ==========================================

export async function startTestCaseUpdate(params: {
  csvBase64: string
  fileName: string
  jiraTicketKeys: string[]
  manualTickets: { key: string; summary: string; description?: string }[]
}): Promise<{ updateJobId: string }> {
  const response = await fetch(`${API_BASE_URL}/test-case-updater/analyze`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(params),
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to start analysis')
  }
  return response.json()
}

export async function getTestCaseUpdateStatus(updateJobId: string): Promise<{
  status: string
  progress: number
  statusMessage?: string
  error?: string
  hasRewrite?: boolean
}> {
  const response = await fetch(`${API_BASE_URL}/test-case-updater/status/${updateJobId}`, {
    headers: getAuthHeaders(),
  })
  if (!response.ok) throw new Error('Failed to get status')
  return response.json()
}

export async function getTestCaseUpdateResults(updateJobId: string): Promise<TCUpdateResults> {
  const response = await fetch(`${API_BASE_URL}/test-case-updater/results/${updateJobId}`, {
    headers: getAuthHeaders(),
  })
  if (!response.ok) throw new Error('Failed to get results')
  return response.json()
}

export async function startTestCaseRewrite(updateJobId: string): Promise<{ updateJobId: string }> {
  const response = await fetch(`${API_BASE_URL}/test-case-updater/rewrite/${updateJobId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to start rewrite')
  }
  return response.json()
}

export async function getTestCaseRewriteResults(updateJobId: string): Promise<TCRewriteResult> {
  const response = await fetch(`${API_BASE_URL}/test-case-updater/rewrite/${updateJobId}`, {
    headers: getAuthHeaders(),
  })
  if (!response.ok) throw new Error('Failed to get rewrite results')
  return response.json()
}

export async function exportTestCaseUpdate(params: {
  updateJobId: string
  exportType: 'analysis' | 'rewrite'
  format: 'testrail' | 'zephyr' | 'csv'
  selectedCaseIds?: string[]
}): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/test-case-updater/export`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(params),
  })
  if (!response.ok) throw new Error('Failed to export')
  return response.text()
}

